import React, { useMemo } from "react";
import { Else, If, Then } from "react-if";
import { Link } from "react-router-dom";
import { URLs } from "@/configs/menu";
import HomeIcon from "@/icons/home.svg?react";

import "./breadcrumb.scss";
import { ISVG } from "@/icons/interface";
import BreadcrumbEntitySwitcher from "./EntitySwitcher/BreadcrumbEntitySwitcher";
import type { BreadcrumbSwitcherRef } from "./EntitySwitcher/types";

const RightArrow = () => (
  <span
    aria-hidden="true"
    className="k-icon k-font-icon k-i-chevron-right k-breadcrumb-delimiter-icon"
    role="presentation"
  />
);

export type BreadcrumbItem = {
  label: string;
  key?: string;
  path?: string;
  Icon?:
    | React.FC<ISVG>
    | React.FC<React.SVGProps<SVGSVGElement>>
    | React.ForwardRefExoticComponent<React.RefAttributes<SVGSVGElement>>
    | string;
  /**
   * Opt-in: renders this crumb as an interactive entity switcher instead of
   * static text.
   */
  switcher?: BreadcrumbSwitcherRef;
};
export type BreadcrumbItems = BreadcrumbItem[];

const Breadcrumb: React.FC<{
  breadcrumbItems: BreadcrumbItems;
}> = ({ breadcrumbItems }) => {
  // Exactly one switcher on the page claims the `/` shortcut.
  const firstSwitcherIndex = useMemo(
    () => breadcrumbItems.findIndex(item => item.switcher),
    [breadcrumbItems],
  );

  return (
    <div className="tlx-breadcrumb-container">
      <Link to={URLs.dashboard} className="link-text home-icon">
        <HomeIcon />
      </Link>
      <If condition={breadcrumbItems.length > 0}>
        <Then>
          <RightArrow />
        </Then>
      </If>

      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const Icon = item.Icon;

        // The switcher renders its own icon and label, so it replaces both the
        // icon container and the label/link below. The delimiter still follows,
        // keeping the crumb count identical to a static trail.
        if (item.switcher) {
          return (
            <React.Fragment key={item.key || item.label}>
              <BreadcrumbEntitySwitcher
                switcher={item.switcher}
                label={item.label}
                icon={Icon}
                isLast={isLast}
                isPrimary={index === firstSwitcherIndex}
              />
              <If condition={!isLast}>
                <Then>
                  <RightArrow />
                </Then>
              </If>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={item.key || item.path || item.label}>
            <If condition={!!Icon}>
              <Then>
                <div className="breadcrumb-icon-container">
                  {Icon && typeof Icon !== "string" && (
                    <Icon height={16} width={16} />
                  )}
                  {Icon && typeof Icon === "string" && (
                    <img alt={item.label} src={Icon} height={14} />
                  )}
                </div>
              </Then>
            </If>

            <If condition={isLast}>
              <Then>
                <span className="breadcrumb-last-item-label">{item.label}</span>
              </Then>
              <Else>
                <If condition={item?.path}>
                  <Then>
                    <Link to={item?.path!} className="link-text">
                      {item.label}
                    </Link>
                  </Then>
                  <Else>
                    <span>{item.label}</span>
                  </Else>
                </If>

                <RightArrow />
              </Else>
            </If>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Breadcrumb;
